/**
 * In-memory Zustand store for ref-master option data.
 *
 * This store is the in-process cache layer that sits above IndexedDB.
 * Data here survives re-renders and component unmounts but is lost on
 * a full page reload (IndexedDB serves as the persistent fallback).
 */

import { create } from 'zustand'
import type { RefOption } from '@/services/refService'

interface RefState {
  /** category key → array of { code, label } */
  cache: Record<string, RefOption[]>

  /** Store options for a category (replaces existing entry if present). */
  setCache: (category: string, options: RefOption[]) => void

  /**
   * Returns the label strings for a category, ready for use as
   * <select> option values.  Returns undefined if the category has
   * not been loaded yet.
   */
  getLabels: (category: string) => string[] | undefined

  /**
   * Look up the API code for a given display label within a category.
   * Used when constructing dependent category keys (e.g. label "Volvo" →
   * code "VOLVO" → category "TRUCK_MODEL_VOLVO").
   */
  getCode: (category: string, label: string) => string | undefined

  /**
   * Look up the numeric ID for a given display label within a category.
   * Used when building the claim payload to convert selected labels → IDs.
   */
  getId: (category: string, label: string) => number | undefined

  /**
   * Look up the display label for a given numeric ID within a category.
   * Used when converting a GET response back into form values.
   */
  getLabelById: (category: string, id: number) => string | undefined

  /** Clear all cached categories from memory (e.g. on New Inspection). */
  clearCache: () => void
}

export const useRefStore = create<RefState>()((set, get) => ({
  cache: {},

  setCache: (category, options) =>
    set((s) => ({ cache: { ...s.cache, [category]: options } })),

  getLabels: (category) => {
    const options = get().cache[category]
    return options ? options.map((o) => o.label) : undefined
  },

  getCode: (category, label) => {
    const options = get().cache[category]
    return options?.find((o) => o.label === label)?.code
  },

  getId: (category, label) => {
    const options = get().cache[category]
    return options?.find((o) => o.label === label)?.id
  },

  getLabelById: (category, id) => {
    const options = get().cache[category]
    return options?.find((o) => o.id === id)?.label
  },

  clearCache: () => set({ cache: {} }),
}))
